// Pure helper functions for sensor alarm logic.
// All business rules (RN1, RN3, RN4, RN5, RN9, CE1, CE2, V2, V4, D3) live here,
// away from JSX, so they are independently testable without React.
//
// Data source: the consolidated monitoring snapshot's `sensorErrors` (a flat array of
// active device-group alarms; each item is { id, code, description, deviceGroupId,
// level, date }). The screen groups them by deviceGroupId and feeds each cell section
// its slice. (The standalone `deviceGroupErrors` endpoint was removed in sun.)

const SEVERITY_ORDER = ['ERR', 'WAR', 'INF']

/**
 * Normalise an error level string.
 * Unknown / absent levels degrade to 'ERR' (CE2: never under-report severity).
 * @param {string|undefined} level
 * @returns {'ERR'|'WAR'|'INF'}
 */
function normalizeLevel (level) {
  if (level === 'INF' || level === 'WAR' || level === 'ERR') return level
  return 'ERR'
}

/**
 * Groups a flat list of device group errors by their deviceGroupId.
 * Keys are stringified so a numeric group id and a string deviceGroupId match.
 * Handles CE1: a missing/undefined list yields an empty map.
 * @param {Array<{deviceGroupId: string|number}>} errors
 * @returns {Object<string, Array>} map of deviceGroupId -> errors
 */
function groupErrorsByDeviceGroupId (errors) {
  const map = {}
  ;(errors ?? []).forEach((error) => {
    const key = String(error.deviceGroupId)
    if (!(key in map)) map[key] = []
    map[key].push(error)
  })
  return map
}

/**
 * Filters the errors that light up the header indicator (D3: only WAR and ERR).
 * INF errors are informational and do NOT trigger the visible badge.
 * @param {Array<{level: string}>} errors
 * @returns {Array<{code: string, description: string, level: string}>}
 */
function getHeaderErrors (errors) {
  return errors.filter((error) => {
    const level = normalizeLevel(error.level)
    return level === 'WAR' || level === 'ERR'
  })
}

/**
 * Returns the highest-severity level found in the given errors array (RN5).
 * ERR > WAR > INF. Returns 'ERR' if the array is empty (safe default).
 * @param {Array<{level: string}>} errors
 * @returns {'ERR'|'WAR'|'INF'}
 */
function getAggregatedLevel (errors) {
  if (errors.length === 0) return 'ERR'
  const normalised = errors.map((error) => normalizeLevel(error.level))
  for (const level of SEVERITY_ORDER) {
    if (normalised.includes(level)) return level
  }
  return 'ERR'
}

/**
 * Counts errors per normalised level. Always returns the three buckets so callers
 * can render a stable summary regardless of which levels are present.
 * @param {Array<{level: string}>} errors
 * @returns {{ERR: number, WAR: number, INF: number}}
 */
function countByLevel (errors) {
  const counts = { ERR: 0, WAR: 0, INF: 0 }
  ;(errors ?? []).forEach((error) => {
    counts[normalizeLevel(error.level)]++
  })
  return counts
}

/**
 * Returns a human-meaningful description, or '' when it would add no information.
 * Graceful degradation (CE3/D6): the backend often stores description === code
 * (catalogue not mapped yet); in that case there is nothing extra to show.
 * @param {{code: string, description?: string}} error
 * @returns {string}
 */
function getDisplayDescription (error) {
  const description = error.description ?? ''
  if (!description || description === error.code) return ''
  return description
}

export {
  normalizeLevel,
  groupErrorsByDeviceGroupId,
  getHeaderErrors,
  getAggregatedLevel,
  countByLevel,
  getDisplayDescription,
  SEVERITY_ORDER
}
